import { Box } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { LoadingOverlay } from '@/components/ui-shared/LoadingOverlay/LoadingOverlay'
import { useWaitForCeleryTaskResultsAsync } from '@/hooks/useWaitForCeleryTaskResultsAsync'
import { useCropDataset } from '@/queries/datasetQueries'
import { showToast } from '@/theme/notifications'
import { ImageCropForm, SubmitValues } from './ImageCropForm'

type ImageCropHandlerProps = {
  targetDatasetVersionId: string
  onCropSuccess: () => void
  onCancel: () => void
}

export const ImageCropHandler = ({
  targetDatasetVersionId,
  onCropSuccess,
  onCancel
}: ImageCropHandlerProps) => {
  const intl = useIntl()

  const { mutateAsync: cropDataset, isPending: isCropping } = useCropDataset()

  const { waitForCeleryTaskResultsAsync, isPending: isPolling } =
    useWaitForCeleryTaskResultsAsync()

  const isProcessing = isCropping || isPolling

  const handleSubmit = async (values: SubmitValues) => {
    try {
      const { task_id } = await cropDataset({
        sourceVersionId: values.sourceDatasetVersionId,
        data: {
          dataset_version_id: targetDatasetVersionId,
          label_ids: values.label_ids
        }
      })

      await waitForCeleryTaskResultsAsync(task_id)
      onCropSuccess()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'datasets.crops.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Box pos="relative" pt="md">
      <LoadingOverlay
        visible={isProcessing}
        message={<FormattedMessage id="datasets.crops.processing" />}
      />

      <ImageCropForm
        onSubmit={(values) => void handleSubmit(values)}
        onCancel={onCancel}
      />
    </Box>
  )
}
